import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LaunchIcon from "@mui/icons-material/Launch";
import clinicalTrialService from "../../services/clinicalTrialsService";

const TrialComparisonPage = () => {
  const [allTrials, setAllTrials] = useState([]);
  const [selectedTrials, setSelectedTrials] = useState([null, null, null]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightDifferences, setHighlightDifferences] = useState(true);

  // Load all trials on mount
  useEffect(() => {
    loadTrials();
  }, []);

  const loadTrials = async () => {
    try {
      setLoading(true);
      const response = await clinicalTrialService.getAllTrials();

      if (response.success) {
        setAllTrials(response.data.trials || response.data.content || []);
      }
    } catch (error) {
      console.error("Error loading trials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrial = (index, trial) => {
    const newSelected = [...selectedTrials];
    newSelected[index] = trial;
    setSelectedTrials(newSelected);
  };

  const handleRemoveTrial = (index) => {
    const newSelected = [...selectedTrials];
    newSelected[index] = null;
    setSelectedTrials(newSelected);
  };

  const handleClearAll = () => {
    setSelectedTrials([null, null, null]);
  };

  const getSelectedCount = () => {
    return selectedTrials.filter((t) => t !== null).length;
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ") || "Unknown";
  };

  const formatPhase = (phase) => {
    if (!phase) return "N/A";
    if (phase === "NA") return "N/A";
    if (phase === "EARLY_PHASE1") return "Early Phase 1";
    return phase.replace("PHASE", "Phase ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "RECRUITING":
        return "success";
      case "NOT_YET_RECRUITING":
        return "info";
      case "COMPLETED":
        return "default";
      case "ACTIVE_NOT_RECRUITING":
        return "warning";
      default:
        return "error";
    }
  };

  // Check if values differ across selected trials
  const hasDifference = (field) => {
    const values = selectedTrials
      .filter((t) => t !== null)
      .map((t) => t[field]);

    if (values.length < 2) return false;

    return !values.every((v) => v === values[0]);
  };

  const getComparisonRows = () => {
    return [
      {
        label: "Trial ID",
        field: "nctId",
        render: (trial) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label={trial.nctId} color="primary" size="small" />
            <Tooltip title="View on ClinicalTrials.gov">
              <IconButton
                size="small"
                href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        label: "Title",
        field: "title",
        render: (trial) => (
          <Typography variant="body2" fontWeight="bold">
            {trial.title || trial.officialTitle}
          </Typography>
        ),
      },
      {
        label: "Status",
        field: "status",
        render: (trial) => (
          <Chip
            label={formatStatus(trial.status)}
            color={getStatusColor(trial.status)}
            size="small"
          />
        ),
      },
      {
        label: "Phase",
        field: "phase",
        render: (trial) => (
          <Chip
            label={formatPhase(trial.phase)}
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        label: "Amyloid Type",
        field: "amyloidType",
        render: (trial) =>
          trial.amyloidType ? (
            <Chip label={trial.amyloidType} color="secondary" size="small" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not specified
            </Typography>
          ),
      },
      {
        label: "Target Organ",
        field: "targetOrgan",
        render: (trial) => trial.targetOrgan || "Not specified",
      },
      {
        label: "Sponsor",
        field: "sponsor",
        render: (trial) => trial.sponsor || "Not specified",
      },
      {
        label: "Start Date",
        field: "startDate",
        render: (trial) => formatDate(trial.startDate),
      },
      {
        label: "Completion Date",
        field: "completionDate",
        render: (trial) => formatDate(trial.completionDate),
      },
      {
        label: "Enrollment",
        field: "enrollment",
        render: (trial) =>
          trial.enrollment
            ? `${trial.enrollment} participants`
            : "Not specified",
      },
      {
        label: "Locations",
        field: "locations",
        render: (trial) => {
          if (!trial.locations) return "Not specified";
          if (Array.isArray(trial.locations)) {
            return `${trial.locations.length} location(s)`;
          }
          return trial.locations;
        },
      },
      {
        label: "Brief Summary",
        field: "briefSummary",
        render: (trial) => (
          <Typography variant="body2" sx={{ maxHeight: 100, overflow: "auto" }}>
            {trial.briefSummary || "Not available"}
          </Typography>
        ),
      },
      {
        label: "Eligibility Criteria",
        field: "eligibilityCriteria",
        render: (trial) => (
          <Typography
            variant="body2"
            sx={{ maxHeight: 150, overflow: "auto", whiteSpace: "pre-line" }}
          >
            {trial.eligibilityCriteria || "Not available"}
          </Typography>
        ),
      },
      {
        label: "Interventions",
        field: "interventions",
        render: (trial) => (
          <Typography variant="body2" sx={{ maxHeight: 100, overflow: "auto" }}>
            {trial.interventions || "Not available"}
          </Typography>
        ),
      },
      {
        label: "Primary Outcome",
        field: "primaryOutcome",
        render: (trial) => (
          <Typography variant="body2" sx={{ maxHeight: 100, overflow: "auto" }}>
            {trial.primaryOutcome || "Not available"}
          </Typography>
        ),
      },
      {
        label: "Secondary Outcomes",
        field: "secondaryOutcome",
        render: (trial) => (
          <Typography variant="body2" sx={{ maxHeight: 100, overflow: "auto" }}>
            {trial.secondaryOutcome || "Not available"}
          </Typography>
        ),
      },
    ];
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    alert(
      "PDF export coming soon! This will generate a downloadable comparison report."
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading trials...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CompareArrowsIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Trial Comparison Tool
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Compare up to 3 clinical trials side-by-side
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Select up to 3 trials from the dropdowns below to compare their details,
        eligibility criteria, and outcomes side-by-side.
      </Alert>

      {/* Trial Selection Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[0, 1, 2].map((index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              elevation={3}
              sx={{
                height: "100%",
                border: selectedTrials[index]
                  ? "2px solid #1A5EA8"
                  : "2px dashed #ccc",
                transition: "all 0.3s",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Trial {index + 1}
                  </Typography>
                  {selectedTrials[index] && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveTrial(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                {!selectedTrials[index] ? (
                  <Autocomplete
                    options={allTrials.filter(
                      (t) => !selectedTrials.some((st) => st?.nctId === t.nctId)
                    )}
                    getOptionLabel={(option) =>
                      `${option.nctId} - ${
                        option.title || option.officialTitle
                      }`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search trials"
                        placeholder="Type to search..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon
                                sx={{ color: "text.secondary", mr: 1 }}
                              />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    onChange={(event, value) =>
                      value && handleAddTrial(index, value)
                    }
                    loading={searchLoading}
                  />
                ) : (
                  <Box>
                    <Chip
                      label={selectedTrials[index].nctId}
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {selectedTrials[index].title ||
                        selectedTrials[index].officialTitle}
                    </Typography>
                    <Box
                      sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={formatStatus(selectedTrials[index].status)}
                        color={getStatusColor(selectedTrials[index].status)}
                        size="small"
                      />
                      <Chip
                        label={formatPhase(selectedTrials[index].phase)}
                        variant="outlined"
                        size="small"
                      />
                      {selectedTrials[index].amyloidType && (
                        <Chip
                          label={selectedTrials[index].amyloidType}
                          color="secondary"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <FormControlLabel
            control={
              <Switch
                checked={highlightDifferences}
                onChange={(e) => setHighlightDifferences(e.target.checked)}
                color="primary"
              />
            }
            label="Highlight Differences"
          />
          <Typography variant="body2" color="text.secondary">
            {getSelectedCount()} of 3 trials selected
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={getSelectedCount() === 0}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={getSelectedCount() < 2}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            Export to PDF
          </Button>
        </Box>
      </Box>

      {/* Comparison Table */}
      {getSelectedCount() >= 2 ? (
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 800 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#1A5EA8",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: 200,
                    }}
                  >
                    Comparison Criteria
                  </TableCell>
                  {selectedTrials.map(
                    (trial, index) =>
                      trial && (
                        <TableCell
                          key={index}
                          sx={{
                            backgroundColor: "#1A5EA8",
                            color: "white",
                            fontWeight: "bold",
                            minWidth: 300,
                          }}
                        >
                          Trial {index + 1}
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {getComparisonRows().map((row, rowIndex) => {
                  const isDifferent =
                    highlightDifferences && hasDifference(row.field);

                  return (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        backgroundColor: isDifferent
                          ? "rgba(255, 152, 0, 0.05)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isDifferent
                            ? "rgba(255, 152, 0, 0.1)"
                            : "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: isDifferent
                            ? "rgba(255, 152, 0, 0.1)"
                            : "rgba(102, 126, 234, 0.05)",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {row.label}
                      </TableCell>
                      {selectedTrials.map(
                        (trial, index) =>
                          trial && (
                            <TableCell
                              key={index}
                              sx={{ verticalAlign: "top" }}
                            >
                              {row.render
                                ? row.render(trial)
                                : trial[row.field] || "N/A"}
                            </TableCell>
                          )
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper
          elevation={2}
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            bgcolor: "background.default",
          }}
        >
          <CompareArrowsIcon
            sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
          />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Select at least 2 trials to compare
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Use the search boxes above to add trials to your comparison
          </Typography>
        </Paper>
      )}

      {/* Legend */}
      {getSelectedCount() >= 2 && highlightDifferences && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="warning" icon={false}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: "rgba(255, 152, 0, 0.2)",
                  border: "1px solid rgba(255, 152, 0, 0.5)",
                }}
              />
              <Typography variant="body2">
                <strong>Highlighted rows</strong> indicate differences between
                the selected trials
              </Typography>
            </Box>
          </Alert>
        </Box>
      )}

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> This comparison tool is for informational
          purposes only. Always consult with your healthcare provider and
          carefully review the full trial details on ClinicalTrials.gov before
          making any decisions about clinical trial participation.
        </Typography>
      </Paper>
    </Container>
  );
};

export default TrialComparisonPage;
